import { z } from 'zod';
import { isoDateSchema, paginated, paginationQuerySchema } from './common.js';

/**
 * Hearth AI: shared shapes for the API and the web app (docs/AI.md). Provider credentials
 * never appear here except as write-only inputs.
 */

// ---------- providers, models, credentials ----------

export const AI_PROVIDERS = ['anthropic', 'openai'] as const;
export const aiProviderSchema = z.enum(AI_PROVIDERS);
export type AiProvider = z.infer<typeof aiProviderSchema>;

/** How each provider's name is written in messages and the UI. */
export const AI_PROVIDER_LABELS: Record<AiProvider, string> = {
  anthropic: 'Anthropic',
  openai: 'OpenAI',
};

/** Where a user's requests go: the panel's own key, or their own (BYOK). */
export const aiSourceSchema = z.enum(['hosted', 'byok']);
export type AiSource = z.infer<typeof aiSourceSchema>;

export const aiModelSchema = z.object({
  provider: aiProviderSchema,
  id: z.string(),
  label: z.string(),
  /** Published list prices in USD per million tokens, or null when not known. */
  inputPerMTok: z.number().nullable(),
  outputPerMTok: z.number().nullable(),
  recommended: z.boolean(),
});
export type AiModel = z.infer<typeof aiModelSchema>;

export const aiCredentialStatusSchema = z.enum(['unverified', 'valid', 'invalid']);

/** A stored BYOK key as the API reports it: never the key itself. */
export const aiCredentialSchema = z.object({
  provider: aiProviderSchema,
  /** Last four characters, e.g. "…a1B2". */
  keyHint: z.string(),
  model: z.string(),
  status: aiCredentialStatusSchema,
  lastError: z.string().nullable(),
  lastCheckedAt: isoDateSchema.nullable(),
  createdAt: isoDateSchema,
});
export type AiCredential = z.infer<typeof aiCredentialSchema>;

export const saveAiCredentialSchema = z.object({
  apiKey: z.string().trim().min(8).max(500),
  model: z.string().trim().min(1).max(100),
});

export const aiCredentialTestResultSchema = z.object({
  ok: z.boolean(),
  status: aiCredentialStatusSchema,
  /** Why the provider refused, in words (never the key). */
  error: z.string().nullable(),
});

export const aiPreferencesSchema = z.object({
  source: aiSourceSchema,
  provider: aiProviderSchema,
  model: z.string().min(1).max(100),
});
export type AiPreferences = z.infer<typeof aiPreferencesSchema>;

export const aiUsageSummarySchema = z.object({
  /** Calendar month (UTC) the totals cover, e.g. "2026-11". */
  period: z.string(),
  requests: z.number().int(),
  inputTokens: z.number().int(),
  outputTokens: z.number().int(),
  /** Sum of known costs; null when every request's cost was unknown. */
  estimatedCostUsd: z.number().nullable(),
  /** Hosted AI only: the monthly token limit and what is left, or null for no limit. */
  hostedLimit: z.number().int().nullable(),
  hostedRemaining: z.number().int().nullable(),
});
export type AiUsageSummary = z.infer<typeof aiUsageSummarySchema>;

/** Everything the AI settings page needs in one call. */
export const aiSettingsViewSchema = z.object({
  preferences: aiPreferencesSchema,
  hosted: z.object({
    /** Whether the provider has configured hosted AI. */
    available: z.boolean(),
    provider: aiProviderSchema.nullable(),
    model: z.string().nullable(),
  }),
  credentials: z.array(aiCredentialSchema),
  models: z.array(aiModelSchema),
  usage: aiUsageSummarySchema,
});
export type AiSettingsView = z.infer<typeof aiSettingsViewSchema>;

/** Provider-admin configuration of hosted AI (the key is write-only). */
export const aiHostedSettingsSchema = z.object({
  enabled: z.boolean(),
  provider: aiProviderSchema,
  model: z.string().min(1).max(100),
  apiKeySet: z.boolean(),
  /** Tokens (input + output) per user per calendar month; null means no limit. */
  monthlyTokenLimitPerUser: z.number().int().positive().nullable(),
  /** Requests per user per minute, hosted and BYOK alike. */
  requestsPerMinute: z.number().int().min(1).max(600),
  /**
   * Custom API base URL for the hosted provider (a gateway or proxy), or null for the
   * provider's own endpoint. Admin-only; BYOK keys always use the provider's endpoint.
   */
  baseUrl: z.string().nullable(),
});
export type AiHostedSettings = z.infer<typeof aiHostedSettingsSchema>;

export const updateAiHostedSettingsSchema = aiHostedSettingsSchema
  .omit({ apiKeySet: true })
  .extend({
    apiKey: z.string().trim().min(8).max(500).optional(),
    clearApiKey: z.boolean().default(false),
    /**
     * Leave out to keep the current value; null goes back to the provider's endpoint. https
     * only. Changing it needs `apiKey` again, so a stored key is never sent to a new host.
     */
    baseUrl: z
      .url({ protocol: /^https$/ })
      .max(500)
      .nullable()
      .optional(),
  });

/** Per user and model for one month (`GET /api/application/ai/usage`). */
export const aiUsageReportQuerySchema = z.object({
  /** Calendar month (UTC), e.g. "2026-11"; defaults to the current month. */
  period: z
    .string()
    .regex(/^\d{4}-(0[1-9]|1[0-2])$/)
    .optional(),
});

export const aiUsageReportRowSchema = z.object({
  userId: z.number().int(),
  username: z.string(),
  source: aiSourceSchema,
  provider: aiProviderSchema,
  model: z.string(),
  requests: z.number().int(),
  inputTokens: z.number().int(),
  outputTokens: z.number().int(),
  estimatedCostUsd: z.number().nullable(),
});

export const aiUsageReportSchema = z.object({
  period: z.string(),
  data: z.array(aiUsageReportRowSchema),
});

// ---------- conversations and messages ----------

/** A tool call as shown in the conversation. */
export const aiToolCallPartSchema = z.object({
  type: z.literal('tool_call'),
  toolCallId: z.string(),
  tool: z.string(),
  /** Human label, e.g. "Read the console". */
  label: z.string(),
  /** The validated input (secrets redacted). */
  input: z.record(z.string(), z.unknown()),
  status: z.enum(['running', 'done', 'failed']),
  /** One-line result for the card; the full output stays server-side. */
  summary: z.string().nullable(),
  error: z.string().nullable(),
});

export const aiMessagePartSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('text'), text: z.string() }),
  aiToolCallPartSchema,
  /** A plan the model proposed; the task carries its status. */
  z.object({ type: z.literal('task'), taskId: z.string().uuid() }),
  /** Something Hearth says, not the model (limits, provider switched, …). */
  z.object({
    type: z.literal('notice'),
    level: z.enum(['info', 'warning', 'error']),
    text: z.string(),
  }),
]);
export type AiMessagePart = z.infer<typeof aiMessagePartSchema>;

export const aiMessageSchema = z.object({
  id: z.string().uuid(),
  role: z.enum(['user', 'assistant']),
  parts: z.array(aiMessagePartSchema),
  status: z.enum(['streaming', 'complete', 'error', 'cancelled']),
  error: z.string().nullable(),
  createdAt: isoDateSchema,
});
export type AiMessage = z.infer<typeof aiMessageSchema>;

/** A server the conversation may work on (resolved from ids the user picked). */
export const aiServerRefSchema = z.object({
  identifier: z.string(),
  name: z.string(),
  game: z.string(),
});

export const aiConversationSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  /** Servers in this conversation's context; the AI can only act on these. */
  servers: z.array(aiServerRefSchema),
  /** Infrastructure context (nodes, all servers): provider admins only. */
  infrastructure: z.boolean(),
  createdAt: isoDateSchema,
  lastMessageAt: isoDateSchema.nullable(),
});
export type AiConversation = z.infer<typeof aiConversationSchema>;

export const aiConversationDetailSchema = aiConversationSchema.extend({
  messages: z.array(aiMessageSchema),
});
export type AiConversationDetail = z.infer<typeof aiConversationDetailSchema>;

export const createAiConversationSchema = z.object({
  title: z.string().trim().min(1).max(120).optional(),
  /** Server identifiers (8 characters) or UUIDs; each is authorised. */
  servers: z.array(z.string().min(8).max(36)).max(10).default([]),
  infrastructure: z.boolean().default(false),
});

export const updateAiConversationSchema = z.object({
  title: z.string().trim().min(1).max(120).optional(),
  servers: z.array(z.string().min(8).max(36)).max(10).optional(),
  infrastructure: z.boolean().optional(),
});

export const aiConversationListQuerySchema = paginationQuerySchema.extend({
  /** Matches the title or the text of any message. */
  search: z.string().trim().max(200).optional(),
});

export const aiConversationListSchema = paginated(aiConversationSchema);

export const sendAiMessageSchema = z.object({
  content: z.string().trim().min(1).max(20_000),
});

/** Server-sent events of one assistant turn (`event: <type>`, `data: <json>`). */
export const aiStreamEventSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('message_start'), messageId: z.string().uuid() }),
  z.object({ type: z.literal('text_delta'), text: z.string() }),
  /** The model is thinking (no text: Hearth never shows raw reasoning). */
  z.object({ type: z.literal('thinking') }),
  z.object({
    type: z.literal('tool_call_start'),
    toolCallId: z.string(),
    tool: z.string(),
    label: z.string(),
  }),
  z.object({
    type: z.literal('tool_call_end'),
    toolCallId: z.string(),
    ok: z.boolean(),
    summary: z.string().nullable(),
    error: z.string().nullable(),
  }),
  z.object({ type: z.literal('task_proposed'), taskId: z.string().uuid() }),
  z.object({
    type: z.literal('notice'),
    level: z.enum(['info', 'warning', 'error']),
    text: z.string(),
  }),
  z.object({
    type: z.literal('usage'),
    inputTokens: z.number().int(),
    outputTokens: z.number().int(),
  }),
  z.object({ type: z.literal('error'), code: z.string(), message: z.string() }),
  /** Last event of every turn; the message is now persisted with this status. */
  z.object({
    type: z.literal('done'),
    status: z.enum(['complete', 'error', 'cancelled']),
  }),
]);
export type AiStreamEvent = z.infer<typeof aiStreamEventSchema>;

// ---------- tools, plans and tasks ----------

/** How much a tool can change: decided by Hearth's policy, never by the model. */
export const aiToolRiskSchema = z.enum(['read', 'write', 'destructive']);
export type AiToolRisk = z.infer<typeof aiToolRiskSchema>;

export const aiToolDescriptorSchema = z.object({
  id: z.string(),
  label: z.string(),
  description: z.string(),
  category: z.enum(['server', 'console', 'files', 'lifecycle', 'game', 'infrastructure', 'plan']),
  risk: aiToolRiskSchema,
  /** Server permissions the caller must hold (owner/admin have all). */
  permissions: z.array(z.string()),
  /** Provider-admin only (infrastructure tools). */
  adminOnly: z.boolean(),
});
export type AiToolDescriptor = z.infer<typeof aiToolDescriptorSchema>;

/** What an action will touch, for the approval screen. Computed by the tool, not the model. */
export const aiActionImpactSchema = z.object({
  files: z.array(z.string()).default([]),
  settings: z.array(z.string()).default([]),
  /** The server will be stopped or restarted. */
  downtime: z.boolean().default(false),
  /** A port or allocation changes, e.g. "Adds port 8123 on 0.0.0.0". */
  network: z.string().nullable().default(null),
  risks: z.array(z.string()).default([]),
  /**
   * A unified diff of what the step writes (redacted; long diffs end with "… N more lines"),
   * or null when it writes no file.
   */
  diff: z.string().nullable().default(null),
  /** Names of secret settings the step sets; their values are never shown. */
  secrets: z.array(z.string()).default([]),
});
export type AiActionImpact = z.infer<typeof aiActionImpactSchema>;
/** What a tool's impact() returns; defaults fill the rest. */
export type AiActionImpactInput = z.input<typeof aiActionImpactSchema>;

/** One step of a plan as the model proposes it. */
export const aiPlanActionInputSchema = z.object({
  tool: z.string().min(1).max(100),
  description: z.string().trim().min(1).max(300),
  input: z.record(z.string(), z.unknown()),
});

/** `propose_plan`'s input: the model's proposal before Hearth validates it. */
export const aiPlanProposalSchema = z.object({
  title: z.string().trim().min(1).max(120),
  summary: z.string().trim().min(1).max(2000),
  /** The server this plan acts on (identifier); must be in the conversation's context. */
  server: z.string().min(8).max(36),
  actions: z.array(aiPlanActionInputSchema).min(1).max(20),
});
export type AiPlanProposal = z.infer<typeof aiPlanProposalSchema>;

/** A validated plan action: risk and approval come from policy. */
export const aiPlanActionSchema = aiPlanActionInputSchema.extend({
  index: z.number().int().min(0),
  label: z.string(),
  risk: aiToolRiskSchema,
  requiresApproval: z.boolean(),
  impact: aiActionImpactSchema,
});
export type AiPlanAction = z.infer<typeof aiPlanActionSchema>;

export const aiPlanSchema = z.object({
  title: z.string(),
  summary: z.string(),
  server: aiServerRefSchema,
  actions: z.array(aiPlanActionSchema),
  /** True when any action is destructive: approval needs the server name typed. */
  strongConfirmation: z.boolean(),
});
export type AiPlan = z.infer<typeof aiPlanSchema>;

export const AI_TASK_STATUSES = [
  'REQUESTED',
  'PLANNING',
  'AWAITING_APPROVAL',
  'APPROVED',
  'EXECUTING',
  'VERIFYING',
  'COMPLETED',
  'REJECTED',
  'CANCELLED',
  'FAILED',
  'PARTIALLY_COMPLETED',
] as const;
export const aiTaskStatusSchema = z.enum(AI_TASK_STATUSES);
export type AiTaskStatus = z.infer<typeof aiTaskStatusSchema>;

/** States after which a task never changes again. */
export const AI_TASK_FINAL: readonly AiTaskStatus[] = [
  'COMPLETED',
  'REJECTED',
  'CANCELLED',
  'FAILED',
  'PARTIALLY_COMPLETED',
];

export const aiTaskStepSchema = z.object({
  index: z.number().int(),
  tool: z.string(),
  label: z.string(),
  description: z.string(),
  status: z.enum(['pending', 'running', 'done', 'failed', 'skipped', 'cancelled']),
  /** Short result for the panel, e.g. "Installed Dynmap 3.7 (dynmap-3.7.jar)". */
  summary: z.string().nullable(),
  error: z.string().nullable(),
  startedAt: isoDateSchema.nullable(),
  finishedAt: isoDateSchema.nullable(),
});
export type AiTaskStep = z.infer<typeof aiTaskStepSchema>;

/** One verified (or not) outcome, reported level by level. */
export const aiVerificationSchema = z.object({
  check: z.string(),
  status: z.enum(['passed', 'failed', 'not_verifiable']),
  detail: z.string(),
});

export const aiTaskSchema = z.object({
  id: z.string().uuid(),
  conversationId: z.string().uuid().nullable(),
  title: z.string(),
  status: aiTaskStatusSchema,
  plan: aiPlanSchema,
  planVersion: z.number().int(),
  /** What the approve call must send back: SHA-256 of the canonical plan. */
  planHash: z.string(),
  steps: z.array(aiTaskStepSchema),
  /** Index of the step running now, or null. */
  currentStep: z.number().int().nullable(),
  approvedBy: z.string().nullable(),
  approvedAt: isoDateSchema.nullable(),
  cancelRequested: z.boolean(),
  verification: z.array(aiVerificationSchema),
  /** Things Hearth couldn't do and the user must (e.g. open a firewall port). */
  followUps: z.array(z.string()),
  error: z.string().nullable(),
  createdAt: isoDateSchema,
  updatedAt: isoDateSchema,
  finishedAt: isoDateSchema.nullable(),
});
export type AiTask = z.infer<typeof aiTaskSchema>;

export const approveAiTaskSchema = z.object({
  planHash: z.string().regex(/^[0-9a-f]{64}$/),
  /** Required (the server's name) when the plan needs strong confirmation. */
  confirmation: z.string().max(200).optional(),
});

export const rejectAiTaskSchema = z.object({
  reason: z.string().trim().max(500).optional(),
});

export const aiTaskListQuerySchema = z.object({
  conversation: z.string().uuid().optional(),
  active: z.coerce.boolean().optional(),
});

// ---------- list responses ----------

/** `GET /api/ai/tasks`: newest first. */
export const aiTaskListSchema = z.object({ data: z.array(aiTaskSchema) });
/** `GET /api/ai/models`. */
export const aiModelListSchema = z.object({ data: z.array(aiModelSchema) });

// ---------- suggestions ----------

/** Starting prompts shown on an empty conversation; each is a real request. */
export const AI_SUGGESTED_PROMPTS = [
  'Install Dynmap on my Minecraft server and set it up so I can view my world in a browser.',
  'Analyse my server’s recent crashes.',
  'Find out why my server is lagging.',
  'Install EssentialsX and configure it.',
  'Create a backup of my server.',
  'Help optimise my server for 50 players.',
  'Explain the latest console errors.',
] as const;
